/**
 * Authoritative Cancel Proposal — shared categories, eligibility, and copy.
 * Cancel ≠ Lost ≠ Archive ≠ Delete.
 */

export const PROPOSAL_CANCELLATION_CATEGORIES = [
  { value: "customer_cancelled", label: "Customer cancelled" },
  { value: "test_or_duplicate", label: "Test or duplicate" },
  { value: "pricing_or_scope_withdrawn", label: "Pricing or scope withdrawn" },
  { value: "unable_to_fulfil", label: "Unable to fulfil" },
  { value: "replaced_by_other_proposal", label: "Replaced by other proposal" },
  { value: "other", label: "Other" },
] as const;

export type ProposalCancellationCategory =
  (typeof PROPOSAL_CANCELLATION_CATEGORIES)[number]["value"];

export const PROPOSAL_CANCELLATION_CATEGORY_VALUES = PROPOSAL_CANCELLATION_CATEGORIES.map(
  (c) => c.value,
);

export type ProposalCancellationSource = "portal_ui" | "cleo" | "admin" | "system";

export const PROPOSAL_CANCELLED_CUSTOMER_MESSAGE =
  "This proposal has been cancelled. Please contact your Clarity representative if you need assistance.";

export function labelForProposalCancellationCategory(
  value: string | null | undefined,
): string {
  const v = String(value ?? "").trim();
  const hit = PROPOSAL_CANCELLATION_CATEGORIES.find((c) => c.value === v);
  return hit?.label ?? (v || "—");
}

export function isProposalCancelled(row: { cancelledAt?: Date | string | null }): boolean {
  return row.cancelledAt != null && String(row.cancelledAt).trim() !== "";
}

export type DestructiveProposalAction = "delete" | "cancel" | "none";

export type ProposalDestructiveEligibilityInput = {
  status?: string | null;
  pipelineStage?: string | null;
  lifecycleStatus?: string | null;
  sentAt?: Date | string | null;
  approvedAt?: Date | string | null;
  cancelledAt?: Date | string | null;
  archivedAt?: Date | string | null;
  /** True when any child revision exists in the family under this id as parent. */
  hasChildRevision?: boolean;
  /** True when any invoice / payment / VO / procurement / service / task obligation exists. */
  hasDownstreamObligation?: boolean;
  /** True when proposal was ever sent or customer-viewed (publicViewCount > 0 counts as issued). */
  publicViewCount?: number | null;
};

/** Genuine unsent draft with no approval, no children, no downstream — hard delete only. */
export function isCleanDeletableDraft(row: ProposalDestructiveEligibilityInput): boolean {
  if (isProposalCancelled(row)) return false;
  if (row.hasChildRevision) return false;
  if (row.hasDownstreamObligation) return false;
  if (row.approvedAt != null && String(row.approvedAt).trim() !== "") return false;
  if (row.sentAt != null && String(row.sentAt).trim() !== "") return false;
  if ((row.publicViewCount ?? 0) > 0) return false;
  const status = String(row.status ?? "").toLowerCase();
  const stage = String(row.pipelineStage ?? "").toLowerCase();
  const lifecycle = String(row.lifecycleStatus ?? "").toLowerCase();
  if (status === "sent" || status === "approved" || status === "rejected") return false;
  if (stage === "prospect" || stage === "projected" || stage === "completed" || stage === "lost") {
    return false;
  }
  if (lifecycle === "sent" || lifecycle === "approved" || lifecycle === "cancelled") return false;
  return status === "draft" || stage === "draft" || !status;
}

/**
 * Which destructive action the workspace should offer.
 * - clean draft → delete
 * - already cancelled → none (badge only)
 * - otherwise commercially issued / downstream → cancel
 */
export function resolveDestructiveProposalAction(
  row: ProposalDestructiveEligibilityInput,
): DestructiveProposalAction {
  if (isProposalCancelled(row)) return "none";
  if (isCleanDeletableDraft(row)) return "delete";
  return "cancel";
}

export function buildCancellationIdempotencyKey(
  familyRootId: number,
  occurrenceIso: string,
): string {
  return `proposal-cancel:family-${familyRootId}:occ-${occurrenceIso}`;
}

export type ProposalCancellationBlocker = {
  code: string;
  message: string;
  kind: "finance" | "procurement" | "vo" | "service" | "group_billing" | "other";
  details?: Record<string, unknown>;
};

export type ProposalCancellationPreflight = {
  proposalId: number;
  proposalRef: string;
  familyProposalIds: number[];
  alreadyCancelled: boolean;
  linksToTerminate: number;
  unpaidInvoicesToVoid: Array<{
    kind: "deal" | "portal" | "svr";
    invoiceId: number;
    invoiceNumber: string | null;
    amount: number | null;
    portalStatus?: string | null;
    xeroInvoiceNumber?: string | null;
    xeroStatus?: string | null;
    /** Planned cancel action for Finance confirmation UI. */
    plannedAction?:
      | "cancel_portal_only"
      | "void_xero_then_portal"
      | "release_allocation_only"
      | "blocked_paid"
      | "shared_do_not_void_parent";
    safety?: "safe" | "blocked" | "shared";
  }>;
  paidOrPartialBlockers: ProposalCancellationBlocker[];
  vosAffected: Array<{ voId: number; voNumber: string | null; status: string; action: string }>;
  procurementBlockers: ProposalCancellationBlocker[];
  serviceBlockers: ProposalCancellationBlocker[];
  remindersToCancel: number;
  tasksToCancel: number;
  signalsToResolve: number;
  futureTranchesToCancel: number;
  unpaidAllocationsToRelease: number;
  /** Proposal-specific group allocations that will be voided without touching the parent invoice. */
  unpaidGroupAllocationsToRelease: Array<{
    allocationId: number;
    parentDealInvoiceId: number;
    amount: number | null;
  }>;
  recordsPreserved: string[];
  blockers: ProposalCancellationBlocker[];
  canApply: boolean;
};
